// Shared cooking-state management for saved meal plans.
//
// Tracks per-recipe cooking progress as { [recipeId]: { startedAt, isCompleted } }
// where recipeId is `recipe.id || recipe.title`. State is fetched once from the
// backend on mount and persisted optimistically via POST on start/complete.
//
// Used by MyMealPlans (card buttons), RecipeDetailPage (Start Cooking) and
// CookingModePage (Complete) so all three share a single source of truth for
// cooking progress.

import { useState, useRef, useCallback, useEffect } from 'react';
import backendClient from '../services/backendClient';

export const useCookingStates = () => {
  const [cookingStates, setCookingStates] = useState({});
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;

    const fetchCookingStates = async () => {
      try {
        const data = await backendClient.get('/api/mealplan/cooking-states');
        setCookingStates(data.states || {});
        hasFetched.current = true;
      } catch (e) {
        console.error('❌ Error fetching cooking states:', e);
      }
    };
    fetchCookingStates();
  }, []);

  const startCooking = useCallback(async (recipeId) => {
    if (!recipeId) return;

    // Optimistically update the UI first.
    setCookingStates((prev) => ({
      ...prev,
      [recipeId]: { startedAt: Date.now(), isCompleted: false },
    }));

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return; // Keep local state only.

      await backendClient.post('/api/mealplan/recipe/start', { recipeId });
    } catch (e) {
      console.error('❌ Error starting cooking:', e);
      if (e.isAuthError) return; // Keep local state, backend not synced.

      // Revert optimistic update for other errors.
      setCookingStates((prev) => {
        const newState = { ...prev };
        delete newState[recipeId];
        return newState;
      });
    }
  }, []);

  const completeCooking = useCallback(async (recipeId) => {
    if (!recipeId) return;

    setCookingStates((prev) => ({
      ...prev,
      [recipeId]: { ...prev[recipeId], isCompleted: true },
    }));

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      await backendClient.post('/api/mealplan/recipe/complete', { recipeId });
    } catch (e) {
      console.error('❌ Error completing cooking:', e);
      if (e.isAuthError) return;

      setCookingStates((prev) => ({
        ...prev,
        [recipeId]: { ...prev[recipeId], isCompleted: false },
      }));
    }
  }, []);

  return { cookingStates, startCooking, completeCooking };
};

export default useCookingStates;
